import React, { useContext, useEffect, useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useHistory } from 'react-router-dom';

import {
  CLEVER_SSO_LOGIN_SOURCE,
  GOOGLE_SSO_LOGIN_SOURCE
} from 'config/constants';
import {
  TEACHERS_ASSIGNMENTS_DETAIL_URL,
  TEACHERS_ASSIGNMENTS_STUDENTS_STATUS_URL,
  TEACHERS_BULK_UPLOAD_ASSIGN_AND_CONFIRMATION_URL,
  TEACHERS_BULK_UPLOAD_ASSIGN_ASSIGNMENT_URL,
  TEACHERS_BULK_UPLOAD_MATCH_STUDENTS_URL,
  TEACHERS_BULK_UPLOAD_PAGE_CONFIRMATION_URL,
  TEACHERS_BULK_UPLOAD_SUBMISSIONS_MATCH_URL,
  TEACHERS_BULK_UPLOAD_SUCCESS_URL,
  TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL,
  TEACHERS_BULK_UPLOAD_WORK_UPLOAD_URL,
  TEACHERS_HOME_URL,
  TEACHERS_MEETINGS_LIST_URL,
  TEACHERS_MEETING_FEED_URL,
  TEACHERS_MEETING_SORT_URL,
  TEACHERS_PERMISSION_DENIED_URL,
  TEACHERS_STUDENTS_DETAIL,
  TEACHERS_STUDENTS_LIST,
  TEACHERS_STUDENT_PORTFOLIO_ASSIGNMENT_DETAILS_URL,
  TEACHERS_STUDENT_PORTFOLIO_URL,
  TEACHERS_TEAM_DETAIL_URL,
  TEACHER_ASSIGNMENTS_V2_URL,
  TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL,
  TEACHER_ASSIGNMENT_V2_INSIGHTS_URL,
  TEACHER_V2_URL,
  WALKTHROUGH_ROLLOUT_URL
} from 'config/urls';
import { get, includes, isUndefined } from 'lodash';
import mixpanel from 'mixpanel-browser';

import Detail from 'pages/Teachers/Assignments/Detail';
import StudentsStatus from 'pages/Teachers/Assignments/StudentsStatus';
import AssignmentsV2 from 'pages/Teachers/AssignmentsV2';
import ConfirmPages from 'pages/Teachers/BulkUpload/ConfirmPages';
import MatchStudents from 'pages/Teachers/BulkUpload/MatchStudents';
import StudentsConfirmation from 'pages/Teachers/BulkUpload/StudentsConfirmation';
import BulkUploadSuccess from 'pages/Teachers/BulkUpload/Success';
import ClassroomInsights from 'pages/Teachers/ClassroomInsights';
import Home from 'pages/Teachers/Home';
import Insights from 'pages/Teachers/Insights';
import StudentDetail from 'pages/Teachers/StudentDetail';
import StudentListV2 from 'pages/Teachers/StudentListV2';
import StudentsList from 'pages/Teachers/StudentsList';
import TeamDetail from 'pages/Teachers/TeamDetail';
import { TeacherPageLayout } from 'pages/Teachers/shared';
import { SIGN_UP_ONBOARDING_STAGES } from 'pages/Walkthrough/constants';
import { useCleverSync } from 'utils/integrations/clever';
import { useGoogleClassroomSync } from 'utils/integrations/googleClassroom';
import { parseParams } from 'utils/urls';

import IntegrationsSyncContext from 'components/IntegrationsSyncContext';
import TeacherRequired from 'components/TeacherRequired';
import UserContext from 'components/UserContext';

import AssignAssignment from './BulkUpload/AssignAssignment';
import ConsolidatedAssignAndConfirm from './BulkUpload/ConsolidatedAssignAndConfirm';
import WorkUpload from './BulkUpload/WorkUpload';
import WorkUploadWithQR from './BulkUpload/WorkUploadWithQR';
import MeetingFeed from './MeetingFeed';
import MeetingSort from './MeetingSort';
import MeetingsList from './MeetingsList';
import NoPermission from './NoPermission';
import StudentPortfolio from './StudentPortfolio';
import StudentPortfolioAssignmentDetails from './StudentPortfolioAssignmentDetails';

const ASSIGNMENT_LIST_NEW_VIEW_ENABLED =
  process.env.REACT_APP_ASSIGNMENT_LIST_NEW_VIEW_ENABLED === 'true';

const Teachers = () => {
  const history = useHistory();

  const googleClassroom = useGoogleClassroomSync();
  const clever = useCleverSync();

  const { user } = useContext(UserContext);

  const searchParams = useMemo(
    () => parseParams(history.location.search),
    [history.location.search]
  );
  const loginSource = useMemo(
    () => get(searchParams, 'source', undefined),
    [searchParams]
  );

  useEffect(() => {
    if (
      !includes([GOOGLE_SSO_LOGIN_SOURCE, CLEVER_SSO_LOGIN_SOURCE], loginSource)
    )
      return;
    if (isUndefined(process.env.REACT_APP_MIXPANEL_KEY)) return;

    mixpanel.identify(String(user.id));
  }, [user, loginSource]);

  useEffect(() => {
    if (!user) {
      return;
    }
    if (
      includes(
        [
          SIGN_UP_ONBOARDING_STAGES.PROFILE_COMPLETED,
          SIGN_UP_ONBOARDING_STAGES.PROFILE_CREATED
        ],
        user.onboarding_stage
      )
    ) {
      history.push(WALKTHROUGH_ROLLOUT_URL);
      return;
    }
  }, [user, history]);

  return (
    <IntegrationsSyncContext.Provider value={{ googleClassroom, clever }}>
      <TeacherPageLayout>
        <Switch>
          <Route exact path={TEACHERS_HOME_URL} component={Home} />
          <Route
            exact
            path={TEACHERS_ASSIGNMENTS_DETAIL_URL}
            component={Detail}
          />

          <Route
            exact
            path={TEACHERS_ASSIGNMENTS_STUDENTS_STATUS_URL}
            component={StudentsStatus}
          />
          <Route path={TEACHER_V2_URL}>
            <Route
              exact
              path={TEACHER_ASSIGNMENTS_V2_URL}
              component={AssignmentsV2}
            />
            <Route
              exact
              path={TEACHER_ASSIGNMENT_V2_INSIGHTS_URL}
              component={Insights}
            />

            <Route
              exact
              path={TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL}
              component={ClassroomInsights}
            />
          </Route>

          <Route
            exact
            path={TEACHERS_STUDENT_PORTFOLIO_URL}
            component={StudentPortfolio}
          />
          <Route
            exact
            path={TEACHERS_STUDENTS_LIST}
            component={
              ASSIGNMENT_LIST_NEW_VIEW_ENABLED ? StudentListV2 : StudentsList
            }
          />
          <Route
            exact
            path={TEACHERS_STUDENTS_DETAIL}
            component={StudentDetail}
          />
          <Route
            exact
            path={TEACHERS_STUDENT_PORTFOLIO_ASSIGNMENT_DETAILS_URL}
            component={StudentPortfolioAssignmentDetails}
          />
          <Route exact path={TEACHERS_TEAM_DETAIL_URL} component={TeamDetail} />
          <Route
            exact
            path={TEACHERS_MEETINGS_LIST_URL}
            component={MeetingsList}
          />
          <Route
            exact
            path={TEACHERS_MEETING_FEED_URL}
            component={MeetingFeed}
          />
          <Route
            exact
            path={TEACHERS_MEETING_SORT_URL}
            component={MeetingSort}
          />
          <Route
            exact
            path={TEACHERS_PERMISSION_DENIED_URL}
            component={NoPermission}
          />
          <Route
            exact
            path={TEACHERS_BULK_UPLOAD_WORK_UPLOAD_URL}
            component={WorkUpload}
          />
          <Route
            exact
            path={TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL}
            component={WorkUploadWithQR}
          />
          <Route
            exact
            path={TEACHERS_BULK_UPLOAD_ASSIGN_ASSIGNMENT_URL}
            component={AssignAssignment}
          />
          <Route
            exact
            path={TEACHERS_BULK_UPLOAD_ASSIGN_AND_CONFIRMATION_URL}
            component={ConsolidatedAssignAndConfirm}
          />
          <Route
            exact
            path={TEACHERS_BULK_UPLOAD_PAGE_CONFIRMATION_URL}
            component={ConfirmPages}
          />
          <Route
            exact
            path={TEACHERS_BULK_UPLOAD_SUBMISSIONS_MATCH_URL}
            component={StudentsConfirmation}
          />
          <Route
            exact
            path={TEACHERS_BULK_UPLOAD_MATCH_STUDENTS_URL}
            component={MatchStudents}
          />
          <Route
            exact
            path={TEACHERS_BULK_UPLOAD_SUCCESS_URL}
            component={BulkUploadSuccess}
          />
          <Route path="*">
            <Redirect to="/" />
          </Route>
        </Switch>
      </TeacherPageLayout>
    </IntegrationsSyncContext.Provider>
  );
};

export default TeacherRequired(Teachers);
